import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * Hook personnalisé pour gérer les appels API avec gestion d'état de chargement et d'erreur
 */
export function useApi<T, P extends any[]>(
  apiFunction: (...args: P) => Promise<T>
) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const execute = useCallback(
    async (...args: P) => {
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await apiFunction(...args);
        setData(result);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [apiFunction]
  );

  return {
    data,
    isLoading,
    error,
    execute
  };
}

/**
 * Hook pour gérer les opérations CRUD avec feedback utilisateur
 */
export function useCrudApi<T, CreateData = any, UpdateData = any>(
  api: {
    getAll?: (params?: any) => Promise<{ items: any[]; total: number }>;
    getById?: (id: string) => Promise<any>;
    create?: (data: CreateData) => Promise<any>;
    update?: (id: string, data: UpdateData) => Promise<any>;
    delete?: (id: string) => Promise<any>;
  },
  options?: {
    successMessages?: {
      create?: string;
      update?: string;
      delete?: string;
    };
    errorMessages?: {
      create?: string;
      update?: string;
      delete?: string;
    };
  }
) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const successMessages = {
    create: options?.successMessages?.create || 'Élément créé avec succès',
    update: options?.successMessages?.update || 'Élément mis à jour avec succès',
    delete: options?.successMessages?.delete || 'Élément supprimé avec succès'
  };

  const errorMessages = {
    create: options?.errorMessages?.create || 'Erreur lors de la création',
    update: options?.errorMessages?.update || 'Erreur lors de la mise à jour',
    delete: options?.errorMessages?.delete || 'Erreur lors de la suppression'
  };

  const fetchItems = useCallback(async (params?: any) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!api.getAll) return;
      const result = await api.getAll(params);
      setItems(result.items);
      setTotal(result.total || 0);
    } catch (err) {
      const error = err as Error;
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const fetchItemById = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!api.getById) throw new Error('getById not implemented');
        const result = await api.getById(id);
        setSelectedItem(result);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [api]
  );

  const createItem = useCallback(
    async (data: CreateData) => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!api.create) throw new Error('create not implemented');
        const result = await api.create(data);
        setItems(prevItems => [...prevItems, result]);
        toast.success(successMessages.create);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        toast.error(errorMessages.create);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [api, successMessages.create, errorMessages.create]
  );

  const updateItem = useCallback(
    async (id: string, data: UpdateData) => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!api.update) throw new Error('update not implemented');
        const result = await api.update(id, data);
        setItems(prevItems => 
          prevItems.map(item => 
            (item as any)._id === id ? result : item
          )
        );
        if (selectedItem && typeof selectedItem === 'object' && '_id' in selectedItem && selectedItem._id === id) {
          setSelectedItem(result);
        }
        toast.success(successMessages.update);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        toast.error(errorMessages.update);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [api, selectedItem, successMessages.update, errorMessages.update]
  );

  const deleteItem = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!api.delete) throw new Error('delete not implemented');
        await api.delete(id);
        setItems(prevItems => 
          prevItems.filter(item => (item as any)._id !== id)
        );
        if (selectedItem && typeof selectedItem === 'object' && '_id' in selectedItem && selectedItem._id === id) {
          setSelectedItem(null);
        }
        toast.success(successMessages.delete);
        return true;
      } catch (err) {
        const error = err as Error;
        setError(error);
        toast.error(errorMessages.delete);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [api, selectedItem, successMessages.delete, errorMessages.delete]
  );

  return {
    items,
    total,
    selectedItem,
    isLoading,
    error,
    fetchItems,
    fetchItemById,
    createItem,
    updateItem,
    deleteItem,
    setSelectedItem
  };
}
